import React, { useState } from 'react';
import { Play } from 'lucide-react';
import { getYouTubeVideoId, getYouTubeThumbnail, sanitizeYouTubeURL } from '@/utils/videoUtils';

interface LazyYouTubeProps {
  url: string;
  className?: string;
  title?: string;
}

export const LazyYouTube: React.FC<LazyYouTubeProps> = ({ 
  url, 
  className = "w-full h-full",
  title = "Video"
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Sanitize URL for better reliability
  const sanitizedUrl = sanitizeYouTubeURL(url);
  const videoId = getYouTubeVideoId(sanitizedUrl);
  
  // Use hqdefault as it's more reliable than maxresdefault
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
  
  const handleImageLoad = () => {
    setImageLoaded(true);
    console.log('YouTube thumbnail loaded successfully for video:', videoId);
  };

  const handleImageError = () => {
    console.error('Failed to load YouTube thumbnail for video:', videoId);
    setImageLoaded(false);
  };

  if (!videoId) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-800 text-gray-400`}>
        Invalid video URL
      </div>
    );
  }

  if (isLoaded) {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&enablejsapi=0&iv_load_policy=3`}
        className={className}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title={title}
        loading="lazy"
      />
    );
  }

  return (
    <div 
      className={`${className} relative cursor-pointer bg-gray-900 flex items-center justify-center group overflow-hidden`}
      onClick={() => setIsLoaded(true)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
      aria-label={`Play video: ${title}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsLoaded(true);
        }
      }}
    >
      {/* YouTube thumbnail */}
      {videoId && thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt={`Video thumbnail: ${title}`}
          className="w-full h-full object-cover"
          loading="eager"
          crossOrigin="anonymous"
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}
      
      {/* Fallback background when no thumbnail or loading fails */}
      {(!videoId || !thumbnailUrl) && (
        <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <Play className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <span className="text-sm">YouTube Video</span>
          </div>
        </div>
      )}
      
      {/* Play button overlay - always on top */}
      <div className={`absolute inset-0 flex items-center justify-center transition-all duration-200 z-10 ${
        isHovered ? 'bg-black bg-opacity-10' : 'bg-black bg-opacity-20'
      }`}>
        <div className={`bg-red-600 rounded-full p-4 shadow-lg transition-transform duration-200 ${
          isHovered ? 'scale-110' : 'scale-100'
        }`}>
          <Play className="w-8 h-8 text-white fill-current ml-1" />
        </div>
      </div>
      
      {/* YouTube facade indicator */}
      <div className="sr-only" data-youtube-facade="true">YouTube Video Facade</div>
    </div>
  );
};